import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/Dashboard/Sidebar/Sidebar';
import Header from '../../components/Dashboard/Header/Header';
import {
  Search,
  Plus,
  SlidersHorizontal as Filters,
  Pencil,
  MoreVertical,
  Eye,
  Trash2,
  Building2,
  Phone,
  Mail,
  MapPin,
  X,
  Loader2
} from 'lucide-react';
import api from '../../api/axios';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import styles from './Vendors.module.css';
import Breadcrumbs from '../../components/Common/Breadcrumbs/Breadcrumbs';
import CustomSelect from '../../components/Common/Select/CustomSelect';
import Pagination from '../../components/Common/Pagination/Pagination';
import VendorDrawer from '../../components/Vendors/VendorDrawer';
import Footer from '../../components/Common/Footer/Footer';
import NoData from '../../components/Common/NoData/NoData';
import { useLoading } from '../../context/LoadingContext';

const MySwal = withReactContent(Swal);

const Vendors = () => {
  const navigate = useNavigate();
  const { loading, showLoader, hideLoader } = useLoading();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [openMenuIndex, setOpenMenuIndex] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    created_by: ''
  });
  const [filterData, setFilterData] = useState({
    users: []
  });

  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    total: 0,
    per_page: 10
  });

  // Permission guards
  const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
  const userPermissions = storedUser.permissions || [];
  const isAdmin = (storedUser.role || '').toLowerCase() === 'admin';
  const canCreate = isAdmin || userPermissions.includes('create vendors');
  const canEdit   = isAdmin || userPermissions.includes('edit vendors');
  const canDelete = isAdmin || userPermissions.includes('delete vendors');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      if (searchTerm !== debouncedSearch) {
        setPagination(prev => ({ ...prev, current_page: 1 }));
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm, debouncedSearch]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.action-wrapper')) {
        setOpenMenuIndex(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  useEffect(() => {
    fetchVendors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.current_page, pagination.per_page, debouncedSearch, filters]);

  useEffect(() => {
    fetchFilterData();
  }, []);

  const fetchVendors = async () => {
    try {
      let url = `/vendors?page=${pagination.current_page}&per_page=${pagination.per_page}&search=${encodeURIComponent(debouncedSearch)}`;
      if (filters.created_by) url += `&created_by=${filters.created_by}`;

      const response = await api.get(url);
      if (response.data.success) {
        setVendors(response.data.data.data);
        const { current_page, last_page, total, per_page } = response.data.data;
        setPagination({ current_page, last_page, total, per_page });
      }
    } catch (error) {
      console.error('Error fetching vendors:', error);
      toast.error('Failed to load vendors');
    }
  };

  const fetchFilterData = async () => {
    try {
      const response = await api.get('/users?per_page=100');
      if (response.data.success) {
        setFilterData({
          users: response.data.data.data || []
        });
      }
    } catch (error) {
      console.error('Error fetching filter data:', error);
    }
  };

  const handleFilterChange = (name, value) => {
    setFilters(prev => ({ ...prev, [name]: value }));
    setPagination(prev => ({ ...prev, current_page: 1 }));
  };

  const resetFilters = () => {
    setFilters({ created_by: '' });
    setPagination(prev => ({ ...prev, current_page: 1 }));
  };

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

  const handleAddClick = () => {
    setEditingVendor(null);
    setIsDrawerOpen(true);
  };

  const handleEditClick = (vendor) => {
    setEditingVendor(vendor);
    setIsDrawerOpen(true);
    setOpenMenuIndex(null);
  };

  const toggleActionMenu = (id, e) => {
    e.stopPropagation();
    if (openMenuIndex === id) {
      setOpenMenuIndex(null);
    } else {
      const rect = e.currentTarget.getBoundingClientRect();
      setMenuPosition({ top: rect.bottom + 4, left: rect.right - 160 });
      setOpenMenuIndex(id);
    }
  };

  const handleDeleteVendor = async (vendor) => {
    setOpenMenuIndex(null);
    
    MySwal.fire({
      showConfirmButton: true,
      showCancelButton: true,
      confirmButtonText: '<div style="display: flex; align-items: center; justify-content: center; gap: 8px;"><svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6h18"></path><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path><line x1="10" y1="11" x2="10" y2="17"></line><line x1="14" y1="11" x2="14" y2="17"></line></svg> Delete Vendor</div>',
      cancelButtonText: 'Cancel',
      buttonsStyling: false,
      customClass: {
        popup: 'custom-swal-popup',
        actions: 'custom-swal-actions',
        confirmButton: 'custom-swal-confirm',
        cancelButton: 'custom-swal-cancel',
      },
      html: `
        <style>
          .custom-swal-popup {
            border-radius: 16px !important;
            padding: 32px 24px 24px 24px !important;
            font-family: 'Outfit', sans-serif !important;
            width: 440px !important;
            box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04) !important;
          }
          @media (max-width: 480px) {
            .custom-swal-popup {
              width: calc(100vw - 32px) !important;
              padding: 24px 16px 20px 16px !important;
            }
            .custom-swal-actions {
              flex-direction: column !important;
              gap: 8px !important;
            }
            .custom-swal-confirm,
            .custom-swal-cancel {
              width: 100% !important;
              justify-content: center !important;
            }
          }
          .custom-swal-actions {
            display: flex !important;
            gap: 12px !important;
            margin-top: 24px !important;
            width: 100% !important;
            padding: 0 !important;
          }
          .custom-swal-confirm {
            background-color: #dc2626 !important;
            color: white !important;
            border: none !important;
            border-radius: 8px !important;
            padding: 10px 24px !important;
            font-size: 14px !important;
            font-weight: 600 !important;
            flex: 1 !important;
            cursor: pointer !important;
            transition: all 0.2s !important;
          }
          .custom-swal-confirm:hover {
            background-color: #b91c1c !important;
          }
          .custom-swal-cancel {
            background-color: #ffffff !important;
            color: #475569 !important;
            border: 1px solid #cbd5e1 !important;
            border-radius: 8px !important;
            padding: 10px 24px !important;
            font-size: 14px !important;
            font-weight: 600 !important;
            flex: 1 !important;
            cursor: pointer !important;
            transition: all 0.2s !important;
          }
          .custom-swal-cancel:hover {
            background-color: #f8fafc !important;
          }
          .custom-icon-wrapper {
            width: 64px;
            height: 64px;
            background-color: #fee2e2;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            margin: 0 auto 20px;
          }
        </style>
        <div class="custom-icon-wrapper">
          <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#dc2626" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"></path>
            <line x1="12" y1="9" x2="12" y2="13"></line>
            <line x1="12" y1="17" x2="12.01" y2="17"></line>
          </svg>
        </div>
        <h2 style="font-size: 20px; font-weight: 700; color: #0f172a; margin: 0 0 12px 0;">Delete Vendor Record?</h2>
        <p style="margin: 0 0 24px 0; color: #475569; font-size: 14px; line-height: 1.5;">
          Are you sure you want to delete <strong>${vendor.vendor_name}</strong>?<br/>
          This action will permanently remove this record from the system.
        </p>
      `
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await api.delete(`/vendors/${vendor.id}`);
          if (response.data.success) {
            toast.success('Vendor deleted successfully');
            fetchVendors();
          }
        } catch (error) {
          toast.error(error.response?.data?.message || 'Error deleting vendor');
        }
      }
    });
  };

  const getInitials = (name) => {
    if (!name) return 'V';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);
  };

  const getAvatarColor = (name) => {
    const colors = [
      '#f97316', '#3b82f6', '#10b981', '#ef4444', '#8b5cf6', 
      '#ec4899', '#f59e0b', '#6366f1', '#14b8a6', '#f43f5e'
    ];
    let hash = 0;
    const str = name || 'Vendor';
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  const getAvatarTextColor = (bgColor) => {
    return '#fff'; // Standard white for premium theme
  };

  return (
    <div className={`${styles['vendors-container']} ${isSidebarOpen ? styles['sidebar-open'] : ''}`}>
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
      {isSidebarOpen && <div className={styles['sidebar-overlay']} onClick={() => setIsSidebarOpen(false)}></div>}

      <div className={styles['content-wrapper']}>
        <Header onMenuClick={toggleSidebar} />

        <main className={styles['main-content']}>
          <Breadcrumbs />

          <div className={styles['page-header']}>
            <div className={styles['header-title-section']}>
              <h1>Vendors</h1>
              <p>View and manage all your suppliers and their contact information from a central directory.</p>
            </div>
            {canCreate && (
              <button className={styles['add-btn']} onClick={handleAddClick}>
                <Plus size={20} />
                <span>Add Vendor</span>
              </button>
            )}
          </div>

          <div className={styles['table-controls']}>
            <div className={styles['search-wrapper']}>
              <Search className={styles['search-icon']} size={18} />
              <input
                type="text"
                placeholder="Search..."
                className={styles['search-input']}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className={styles['filter-actions']}>
              <div className={styles['per-page-select-wrapper']}>
                <CustomSelect
                  isSearchable={false}
                  backgroundColor="white"
                  value={{ value: pagination.per_page, label: `${pagination.per_page} per page` }}
                  onChange={(option) => setPagination(prev => ({ ...prev, per_page: option.value, current_page: 1 }))}
                  options={[
                    { value: 10, label: '10 per page' },
                    { value: 25, label: '25 per page' },
                    { value: 50, label: '50 per page' },
                    { value: 100, label: '100 per page' }
                  ]}
                 />
              </div>
              <button 
                className={`${styles['filter-btn']} ${showFilters ? styles['active'] : ''}`}
                onClick={() => setShowFilters(!showFilters)}
              >
                <Filters size={18} />
                <span>Filters</span>
                {filters.created_by && <span className={styles['filter-badge']}></span>}
              </button>
            </div>
          </div>

          {showFilters && (
            <div className={styles['filters-panel']}>
              <div className={styles['filters-header']}>
                <div className={styles['filters-title-group']}>
                  <h3>Filter Vendors</h3>
                  <p>Narrow down your vendor list with specific criteria.</p>
                </div>
                <button className={styles['close-icon-btn']} onClick={() => setShowFilters(false)}>
                  <X size={18} />
                </button>
              </div>
              <div className={styles['filters-grid']}>
                <div className={styles['filter-group']}>
                  <label>Created By</label>
                  <CustomSelect
                    value={filters.created_by ? { 
                      value: filters.created_by, 
                      label: filterData.users.find(u => u.id === parseInt(filters.created_by))?.name 
                    } : null}
                    onChange={(opt) => handleFilterChange('created_by', opt ? opt.value : '')}
                    options={filterData.users.map(u => ({ value: u.id, label: u.name }))}
                    placeholder="All Users"
                    isClearable={true}
                  />
                </div>
              </div>
              <div className={styles['filters-footer']}>
                <button className={styles['reset-btn']} onClick={resetFilters}>Reset</button>
                <button className={styles['close-btn']} onClick={() => setShowFilters(false)}>Close Filters</button>
              </div>
            </div>
          )}

          <div className={styles['data-view-container']}>
            <div className={styles['table-wrapper']}>
              <table className={styles['vendor-table']}>
                <thead>
                  <tr>
                    <th>VENDOR & ID</th>
                    <th>CONTACT PERSON</th>
                    <th>PHONE</th>
                    <th>EMAIL</th>
                    <th>REGISTERED BY</th>
                    <th className={styles['actions-cell']}>ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {vendors.length > 0 ? (
                    vendors.map((vendor) => (
                          <tr key={vendor.id}>
                            <td className={styles['name-cell']}>
                              <div className={styles['user-info']}>
                                <div 
                                  className={styles['avatar']}
                                  style={{
                                    backgroundColor: getAvatarColor(vendor.vendor_name),
                                    color: getAvatarTextColor(getAvatarColor(vendor.vendor_name))
                                  }}
                                >
                                  {getInitials(vendor.vendor_name)}
                                </div>
                                <div className={styles['name-code-wrapper']}>
                                  <span className={styles['name-text']}>
                                    {vendor.vendor_name}
                                  </span>
                                  {vendor.gst_number && <span className={styles['gst-badge']}>GST: {vendor.gst_number}</span>}
                                </div>
                              </div>
                            </td>
                            <td>{vendor.contact_person_name || 'N/A'}</td>
                            <td>{vendor.phone}</td>
                            <td>{vendor.email || 'N/A'}</td>
                            <td>{vendor.creator?.name || 'N/A'}</td>
                            <td className={styles['actions-cell']}>
                              <div className="action-wrapper">
                                <button
                                  className={`${styles['action-btn']} ${openMenuIndex === vendor.id ? styles['active'] : ''}`}
                                  onClick={(e) => toggleActionMenu(vendor.id, e)}
                                >
                                  <MoreVertical size={18} />
                                </button>

                                {openMenuIndex === vendor.id && (
                                  <div
                                    className={[styles['action-dropdown'], styles['shadow-premium']].join(' ')}
                                    style={{ top: menuPosition.top, left: menuPosition.left }}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <button className={styles['dropdown-item']} onClick={() => navigate(`/vendors/view/${vendor.id}`)}>
                                      <Eye size={16} /> View
                                    </button>
                                    {canEdit && (
                                      <button className={styles['dropdown-item']} onClick={() => handleEditClick(vendor)}>
                                        <Pencil size={16} /> Edit
                                      </button>
                                    )}
                                    {canDelete && (
                                      <>
                                        <div className={styles['dropdown-divider']}></div>
                                        <button 
                                          className={[styles['dropdown-item'], styles['delete-opt']].join(' ')} 
                                          onClick={() => {
                                            handleDeleteVendor(vendor);
                                          }}
                                        >
                                          <Trash2 size={16} /> Delete
                                        </button>
                                      </>
                                    )}
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : !loading && (
                        <NoData message="No vendors found." colSpan={6} isTable={true} />
                      )}
                    </tbody>
                  </table>
                </div>

                <div className={styles['vendor-cards']}>
                  {vendors.length > 0 ? (
                    vendors.map((vendor) => (
                    <div key={vendor.id} className={styles['vendor-card']}>
                      <div className={styles['card-main']}>
                        <div 
                          className={styles['card-avatar']}
                          style={{
                            backgroundColor: getAvatarColor(vendor.vendor_name),
                            color: getAvatarTextColor(getAvatarColor(vendor.vendor_name))
                          }}
                        >
                          {getInitials(vendor.vendor_name)}
                        </div>
                        <div className={styles['card-info']}>
                          <span className={styles['card-name']}>{vendor.vendor_name}</span>
                          {vendor.gst_number && <span className={styles['gst-badge']}>GST: {vendor.gst_number}</span>}
                        </div>
                        <div className={`action-wrapper ${styles['action-wrapper']}`}>
                          <button
                            className={`${styles['action-btn']} ${openMenuIndex === vendor.id ? styles['active'] : ''}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleActionMenu(vendor.id, e);
                            }}
                          >
                            <MoreVertical size={18} />
                          </button>

                          {openMenuIndex === vendor.id && (
                            <div
                              className={[styles['action-dropdown'], styles['shadow-premium']].join(' ')}
                              style={{ top: menuPosition.top, left: menuPosition.left }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <button className={styles['dropdown-item']} onClick={() => navigate(`/vendors/view/${vendor.id}`)}>
                                <Eye size={16} /> View
                              </button>
                              {canEdit && (
                                <button className={styles['dropdown-item']} onClick={() => {
                                  handleEditClick(vendor);
                                  setOpenMenuIndex(null);
                                }}>
                                  <Pencil size={16} /> Edit
                                </button>
                              )}
                              {canDelete && (
                                <>
                                  <div className={styles['dropdown-divider']}></div>
                                  <button 
                                    className={[styles['dropdown-item'], styles['delete-opt']].join(' ')} 
                                    onClick={() => {
                                      handleDeleteVendor(vendor);
                                    }}
                                  >
                                    <Trash2 size={16} /> Delete
                                  </button>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className={styles['card-details']}>
                        <div className={styles['detail-item']}>
                          <span className={styles['detail-label']}>Contact</span>
                          <span className={styles['detail-value']}>{vendor.contact_person_name || 'N/A'}</span>
                        </div>
                        <div className={styles['detail-item']}>
                          <span className={styles['detail-label']}>GST</span>
                          <span className={styles['detail-value']}>{vendor.gst_number || 'N/A'}</span>
                        </div>
                        <div className={styles['detail-item']}>
                          <span className={styles['detail-label']}>Phone</span>
                          <span className={styles['detail-value']}>{vendor.phone}</span>
                        </div>
                        <div className={styles['detail-item']}>
                          <span className={styles['detail-label']}>Email</span>
                          <span className={styles['detail-value']}>{vendor.email || 'N/A'}</span>
                        </div>
                        <div className={styles['detail-item']}>
                          <span className={styles['detail-label']}>Registered By</span>
                          <span className={styles['detail-value']}>{vendor.creator?.name || 'N/A'}</span>
                        </div>
                      </div>
                    </div>
                  ))
                ) : !loading && (
                  <NoData message="No vendors found." isTable={false} />
                )}
              </div>

              <div className={styles['desktop-pagination']}>
                <Pagination
                  currentPage={pagination.current_page}
                  totalItems={pagination.total}
                  itemsPerPage={pagination.per_page}
                  onPageChange={(page) => setPagination(prev => ({ ...prev, current_page: page }))}
                />
              </div>
            </div>

            <div className={styles['mobile-pagination']}>
              <Pagination
                currentPage={pagination.current_page}
                totalItems={pagination.total}
                itemsPerPage={pagination.per_page}
                onPageChange={(page) => setPagination(prev => ({ ...prev, current_page: page }))}
              />
            </div>
        
          <Footer />
        </main>
      </div>

      <VendorDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        initialData={editingVendor}
        onSave={fetchVendors}
      />
    </div>
  );
};

export default Vendors;
